import { useMemo } from "react";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QueryFailure, ScreenHeader } from "@/components/StudioPrimitives";
import { useActiveWorkspace } from "@/lib/workspace/ActiveWorkspace";
import { STUDIO_QK, studioApi } from "@/lib/api/studio";
import { useListRestoration } from "@/lib/studio/useListRestoration";
import { cn } from "@/lib/cn";
import {
  CharacterCardView,
  CharacterGridSkeleton,
} from "./characters/CharacterCardView";

/**
 * The roster's one filter is its search. It lists accepted characters only:
 * drafts live under Drafts, and archived characters have a place of their own
 * at links.characterArchive.
 */
export function characterFiltersFromSearch(params: URLSearchParams) {
  return { q: params.get("q") || undefined };
}

export function CharactersScreen() {
  // WorkspaceLayout resolves the URL slug against /v1/me before this screen
  // mounts, so every request below is scoped to an existing membership.
  const { workspace, slug: orgSlug, links } = useActiveWorkspace();
  const [params, setParams] = useSearchParams();
  const filters = useMemo(() => characterFiltersFromSearch(params), [params]);
  const restorationKey = `characters:${orgSlug}:${params.toString()}`;

  const list = useInfiniteQuery({
    queryKey: STUDIO_QK.characterList(orgSlug, filters),
    queryFn: ({ pageParam }) =>
      studioApi.listCharacters(orgSlug, {
        ...filters,
        cursor: pageParam === "" ? undefined : pageParam,
        limit: 40,
      }),
    initialPageParam: "",
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  });
  useListRestoration({
    key: restorationKey,
    cardIDPrefix: "character-card-",
    pageCount: list.data?.pages.length ?? 0,
    hasNextPage: Boolean(list.hasNextPage),
    isFetchingNextPage: list.isFetchingNextPage,
    fetchNextPage: list.fetchNextPage,
  });

  const characters = list.data?.pages.flatMap((page) => page.characters) ?? [];
  const counts = list.data?.pages[0]?.counts;
  const filtered = Object.values(filters).some(Boolean);
  const emptyResult =
    !list.isPending && !list.isError && characters.length === 0;

  // The listed counts honour the active search, so they cannot answer "how
  // many exist" when a filter matched nothing. One unfiltered page of size 1
  // answers it, and is only fetched in exactly that case.
  const libraryTotal = useQuery({
    queryKey: STUDIO_QK.allCharacters(orgSlug, "listing-total", {}),
    queryFn: () => studioApi.listCharacters(orgSlug, { limit: 1 }),
    enabled: emptyResult && filtered,
  });
  const totalCharacters = libraryTotal.data
    ? libraryTotal.data.counts.accepted + libraryTotal.data.counts.archived
    : undefined;

  // Drafts are not on this page, so a way to them must never depend on what
  // the roster holds or matches: its own one-row read supplies the count.
  const draftTotal = useQuery({
    queryKey: STUDIO_QK.draftList(orgSlug, { limit: 1 }),
    queryFn: () => studioApi.listDrafts(orgSlug, { limit: 1 }),
  });
  const draftCount = draftTotal.data?.counts.drafts;

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  return (
    <section className="space-y-6 px-4 py-8 sm:px-6 sm:py-10">
      <ScreenHeader
        eyebrow="Shared character library"
        title="Characters"
        description={`Accepted identities, shared with every member of ${workspace.display_name}.`}
        actions={
          <>
            <Button asChild variant="outline">
              <Link to={links.drafts}>
                {draftCount === undefined ? "Drafts" : `Drafts (${draftCount})`}
              </Link>
            </Button>
            <Button asChild>
              <Link to={links.newDraft}>
                <Plus className="size-4" aria-hidden="true" />
                New character
              </Link>
            </Button>
          </>
        }
      />

      {counts && (
        <dl className="grid grid-cols-2 gap-2 sm:max-w-sm">
          <Count label="Accepted" value={counts.accepted} />
          <Count
            label="Archived"
            value={counts.archived}
            to={links.characterArchive}
          />
        </dl>
      )}

      <div className="rounded-xl border border-border bg-bg-elevated p-3">
        <label className="relative block">
          <span className="sr-only">Search names and personas</span>
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-fg-subtle"
            aria-hidden="true"
          />
          <input
            type="search"
            value={filters.q ?? ""}
            onChange={(event) => updateParam("q", event.target.value)}
            placeholder="Search name or persona"
            className="h-10 w-full rounded-md border border-border bg-bg pl-9 pr-3 text-sm"
          />
        </label>
      </div>

      {list.isPending && <CharacterGridSkeleton />}
      {list.isError && (
        <QueryFailure
          error={list.error}
          retry={() => {
            void list.refetch();
          }}
          title="Characters could not be loaded."
          headingLevel="h2"
        />
      )}
      {emptyResult && filtered && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <h2 className="text-lg font-semibold">
            No character matches this search.
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-fg-muted">
            {totalCharacters === undefined
              ? `Clear the search to see every character in ${workspace.display_name}.`
              : `${totalCharacters} ${totalCharacters === 1 ? "character" : "characters"} in ${workspace.display_name}. Clear the search to see them.`}
          </p>
          <Button
            variant="outline"
            className="mt-5"
            onClick={() => {
              setParams(new URLSearchParams(), { replace: true });
            }}
          >
            Clear search
          </Button>
        </div>
      )}
      {emptyResult && !filtered && (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <h2 className="text-lg font-semibold">
            Start the shared character library.
          </h2>
          <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-fg-muted">
            {workspace.display_name} has no accepted characters yet. Create a
            draft from inspirations or a written description. Every organization
            member can see it from its first save.
          </p>
          <Button asChild className="mt-5">
            <Link to={links.newDraft}>Create a character</Link>
          </Button>
        </div>
      )}
      {characters.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {characters.map((character) => (
            <CharacterCardView
              key={character.id}
              character={character}
              orgSlug={orgSlug}
              restorationKey={restorationKey}
            />
          ))}
        </div>
      )}

      {list.hasNextPage && (
        <div className="flex justify-center">
          <Button
            variant="outline"
            loading={list.isFetchingNextPage}
            onClick={() => {
              void list.fetchNextPage();
            }}
          >
            Load more
          </Button>
        </div>
      )}
    </section>
  );
}

function Count({
  label,
  value,
  to,
}: {
  label: string;
  value: number;
  to?: string;
}) {
  // A link to an empty archive is a dead end, so the count only becomes a way
  // in once there is something behind it.
  const linked = to !== undefined && value > 0;
  return (
    <div
      className={cn(
        "relative rounded-lg border border-border bg-bg-elevated px-3 py-2",
        linked &&
          "transition-colors hover:border-border-strong focus-within:border-border-strong motion-reduce:transition-none",
      )}
    >
      <dt className="text-[11px] text-fg-subtle">{label}</dt>
      <dd className="font-mono text-lg">
        {linked ? (
          <Link
            to={to}
            aria-label={`Open the archive — ${value} archived ${value === 1 ? "character" : "characters"}`}
            className="rounded-sm after:absolute after:inset-0 after:content-['']"
          >
            {value}
          </Link>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}
